import {
  ChevronDown,
  ChevronUp,
  FileText,
  Image,
  Link as LinkIcon,
  Minus,
  Music,
  PlayCircle,
  Plus,
  Trash2,
  Type,
  Video,
  type LucideIcon,
} from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { newBlock, type AssetUploader } from '../lib/blocks';
import type { LessonBlock, LessonBlockType, LinkBlockData } from '../types';
import { AssetBlockEditor } from './AssetBlockEditor';
import { RichTextBlockEditor } from './RichTextBlockEditor';
import { YoutubeBlockEditor } from './YoutubeBlockEditor';

const BLOCK_MENU: { type: LessonBlockType; icon: LucideIcon }[] = [
  { type: 'rich_text', icon: Type },
  { type: 'image', icon: Image },
  { type: 'video', icon: Video },
  { type: 'audio', icon: Music },
  { type: 'file', icon: FileText },
  { type: 'youtube', icon: PlayCircle },
  { type: 'link', icon: LinkIcon },
  { type: 'divider', icon: Minus },
];

function LinkBlockEditor({
  data,
  onChange,
}: {
  data: LinkBlockData;
  onChange: (data: LinkBlockData) => void;
}) {
  const { t } = useTranslation(['curriculum']);
  const inputId = useId();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor={inputId}>{t('curriculum:editor.link.url')}</Label>
        <Input
          id={inputId}
          value={data.url}
          placeholder="https://…"
          onChange={e => onChange({ ...data, url: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${inputId}-label`}>{t('curriculum:editor.link.label')}</Label>
        <Input
          id={`${inputId}-label`}
          value={data.label ?? ''}
          maxLength={500}
          onChange={e => onChange({ ...data, label: e.target.value })}
        />
      </div>
    </div>
  );
}

function BlockBody({
  block,
  onData,
  upload,
}: {
  block: LessonBlock;
  onData: (data: LessonBlock['data']) => void;
  upload?: AssetUploader;
}) {
  switch (block.type) {
    case 'rich_text':
      return <RichTextBlockEditor value={block.data.html} onChange={html => onData({ html })} />;
    case 'image':
    case 'video':
    case 'audio':
    case 'file':
      return (
        <AssetBlockEditor kind={block.type} data={block.data} onChange={onData} upload={upload} />
      );
    case 'youtube':
      return <YoutubeBlockEditor data={block.data} onChange={onData} />;
    case 'link':
      return <LinkBlockEditor data={block.data} onChange={onData} />;
    case 'divider':
      return <Separator />;
  }
}

interface BlockEditorProps {
  value: LessonBlock[];
  onChange: (blocks: LessonBlock[]) => void;
  /** Defaults to the lesson-asset endpoint. */
  upload?: AssetUploader;
}

/**
 * Ordered lesson-block editor (FE-ADR-011): add menu, per-block move
 * up/down and delete. Emits the backend block shapes
 * (curriculum/blocks.py) — read-enrichment is stripped at save time.
 */
export function BlockEditor({ value, onChange, upload }: BlockEditorProps) {
  const { t } = useTranslation(['curriculum']);

  function updateData(id: string, data: LessonBlock['data']) {
    onChange(value.map(b => (b.id === id ? ({ ...b, data } as LessonBlock) : b)));
  }

  function move(index: number, delta: -1 | 1) {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onChange(next);
  }

  function remove(id: string) {
    onChange(value.filter(b => b.id !== id));
  }

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed px-4 py-6 text-center text-sm">
          {t('curriculum:editor.blocks.empty')}
        </p>
      ) : (
        <ol className="space-y-3">
          {value.map((block, index) => (
            <li key={block.id} className="bg-card rounded-md border">
              <div className="text-muted-foreground flex items-center gap-1 border-b px-3 py-1.5 text-xs font-medium tracking-wide uppercase">
                {t(`curriculum:blocks.${block.type}`)}
                <span className="flex-1" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={index === 0}
                  aria-label={t('curriculum:editor.blocks.moveUp')}
                  onClick={() => move(index, -1)}
                >
                  <ChevronUp />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={index === value.length - 1}
                  aria-label={t('curriculum:editor.blocks.moveDown')}
                  onClick={() => move(index, 1)}
                >
                  <ChevronDown />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('curriculum:editor.blocks.delete')}
                  onClick={() => remove(block.id)}
                >
                  <Trash2 />
                </Button>
              </div>
              <div className="p-3">
                <BlockBody
                  block={block}
                  onData={data => updateData(block.id, data)}
                  upload={upload}
                />
              </div>
            </li>
          ))}
        </ol>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline">
            <Plus />
            {t('curriculum:editor.blocks.add')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {BLOCK_MENU.map(({ type, icon: Icon }) => (
            <DropdownMenuItem key={type} onSelect={() => onChange([...value, newBlock(type)])}>
              <Icon />
              {t(`curriculum:blocks.${type}`)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
